import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ageFromBirthDate } from '../../services/dateFormats';
import {
  Box, Grid, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Stack, Avatar, Chip, Button, TextField, MenuItem, CircularProgress, Tabs, Tab,
  Divider, LinearProgress, Card, CardContent, Switch, FormControlLabel, Snackbar, Alert,
} from '@mui/material';
import SaveRoundedIcon      from '@mui/icons-material/SaveRounded';
import LockRoundedIcon       from '@mui/icons-material/LockRounded';
import DownloadIcon         from '@mui/icons-material/Download';
import PersonIcon           from '@mui/icons-material/Person';
import AccessTimeIcon       from '@mui/icons-material/AccessTime';
import EventAvailableIcon   from '@mui/icons-material/EventAvailable';
import BeachAccessIcon      from '@mui/icons-material/BeachAccess';
import PaymentsIcon         from '@mui/icons-material/Payments';
import BadgeIcon            from '@mui/icons-material/Badge';
import CheckCircleIcon      from '@mui/icons-material/CheckCircle';
import CancelIcon           from '@mui/icons-material/Cancel';
import WatchLaterIcon       from '@mui/icons-material/WatchLater';
import * as XLSX            from 'xlsx';
import jsPDF                from 'jspdf';
import autoTable            from 'jspdf-autotable';
import apiService, { authFetch } from '../../services/apiServices';
import presenceService      from '../../services/presenceService';
import globals              from '../../services/globals';
import usePermissions       from '../../hooks/usePermissions';
import PageHeader           from '../common/PageHeader';
import { ReportPage, ExportMenu } from './ReportKit';
import WorkerAssetsPanel from '../assets/WorkerAssetsPanel';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { activeWorkers } from '../../utils/workerStatus';

// ── helpers ──────────────────────────────────────────────────────────────────
const todayStr   = () => new Date().toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };
function fmtHHMM(min) {
  const m = Math.max(0, Math.round(min));
  return `${Math.floor(m / 60)}h ${('0' + (m % 60)).slice(-2)}m`;
}
function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toTimeString().slice(0, 5);
}
function fmtDate(str) {
  if (!str) return '—';
  try { return new Date(str).toLocaleDateString('sq-AL', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return str; }
}
function initials(name) {
  return (name || '').split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2);
}
function breakMin(breaks) {
  return (breaks || []).reduce((s, b) => {
    const st = b.start ? new Date(b.start).getTime() : NaN;
    const en = b.end   ? new Date(b.end).getTime()   : NaN;
    return s + (!isNaN(st) && !isNaN(en) && en > st ? Math.round((en - st) / 60000) : 0);
  }, 0);
}
function eventsToSessions(events) {
  const groups = new Map();
  (events || []).forEach(e => {
    const at  = new Date(e.At || e.at);
    if (isNaN(at)) return;
    const day = at.toISOString().slice(0, 10);
    const id  = String(e.WorkerId || e.workerId || '');
    if (!id) return;
    const key = day + '__' + id;
    if (!groups.has(key)) groups.set(key, { day, workerId: id, checkIn: null, checkOut: null, breaks: [] });
    const g = groups.get(key);
    const t = (e.Type || e.type || '').toLowerCase();
    if (t === 'checkin'  && !g.checkIn)  g.checkIn  = e.At || e.at;
    if (t === 'checkout' && !g.checkOut) g.checkOut = e.At || e.at;
    if (t === 'breakstart') g.breaks.push({ start: e.At || e.at });
    if (t === 'breakend' && g.breaks.length) { const b = g.breaks.findLast(x => !x.end); if (b) b.end = e.At || e.at; }
  });
  return [...groups.values()].sort((a, b) => b.day.localeCompare(a.day));
}
const LEAVE_TYPE_KEYS = { 1:'report.leaveAnnual', 2:'report.leaveMedical', 3:'report.leaveMarital', 4:'report.leaveUnpaid', 5:'report.leaveExceptional', 6:'report.leavePaid', 7:'report.leaveBlood' };
const LATE_THRESHOLD = '09:00';
function attendStatus(checkIn, t) {
  if (!checkIn) return { label: t('report.absent'),   color: 'error'   };
  const tm = fmtTime(checkIn);
  if (tm > LATE_THRESHOLD) return { label: t('report.late'),     color: 'warning' };
  return { label: t('report.present'), color: 'success' };
}
const STANDARD_MIN = 480;

// ── stat card ─────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color = '#2563EB', sub }) {
  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box sx={{ bgcolor: `${color}18`, color, width: 32, height: 32, minWidth: 32, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.15, color: '#0F172A' }}>{value}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', lineHeight: 1.25 }}>{label}</Typography>
            {sub && <Typography variant="caption" display="block" color="text.disabled" sx={{ fontSize: '0.66rem' }}>{sub}</Typography>}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── info row ──────────────────────────────────────────────────────────────────
function InfoRow({ label, value }) {
  return (
    <Stack direction="row" spacing={1} sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'grey.100' }}>
      <Typography variant="caption" color="text.disabled" sx={{ minWidth: 140, fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.5, pt: 0.1 }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.82rem', color: '#1E293B' }}>{value || '—'}</Typography>
    </Stack>
  );
}

// ── TabPanel ──────────────────────────────────────────────────────────────────
function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

// ─────────────────────────────────────────────────────────────────────────────
export default function EmployeeDetailReport() {
  const { t } = useTranslation();
  const { hasAnyPermission } = usePermissions();
  // Managers/admins may inspect any worker; everyone else is locked to their own card.
  const canViewOthers = hasAnyPermission(['employees.view.department', 'employees.view.company', 'employees.view.all']);
  const myWorkerId = (() => { const u = globals().getLogedInUser?.(); return u?.data?.worker_id || u?.data?.id || ''; })();
  const leaveTypes = useMemo(() => Object.fromEntries(Object.entries(LEAVE_TYPE_KEYS).map(([k, v]) => [k, t(v)])), [t]);
  const [workers,     setWorkers]     = useState([]);
  const [departments, setDepartments] = useState([]);
  const [branches,    setBranches]    = useState([]);
  const [projects,    setProjects]    = useState([]);
  const [selectedId,  setSelectedId]  = useState('');
  const [dateFrom,    setDateFrom]    = useState(monthStart());
  const [dateTo,      setDateTo]      = useState(todayStr());
  const [loading,     setLoading]     = useState(false);
  const [tab,         setTab]         = useState(0);

  // per-worker data
  const [timeEntries, setTimeEntries] = useState([]);
  const [presEvents,  setPresEvents]  = useState([]);
  const [holidays,    setHolidays]    = useState([]);
  const [contract,    setContract]    = useState(null);
  const [salary,      setSalary]      = useState(null);
  const [projectAssignments, setProjectAssignments] = useState([]);

  // load static lists
  useEffect(() => {
    async function loadLists() {
      const [wR, dR, bR, pR] = await Promise.all([
        apiService().get('workers'),
        apiService().get('department'),
        apiService().get('branches'),
        apiService().get('projects').catch(() => ({ success: false })),
      ]);
      if (wR.success)  setWorkers(wR.data || []);
      if (dR.success)  setDepartments(dR.data || []);
      if (bR.success)  setBranches(bR.data || []);
      if (pR.success)  setProjects(pR.data || []);
    }
    loadLists();
  }, []);

  const loadWorkerData = useCallback(async (workerId) => {
    if (!workerId) return;
    setLoading(true);
    try {
      const [teR, evR, hR, cR, sR] = await Promise.all([
        apiService().get('timeEntries'),
        presenceService().getEvents ? presenceService().getEvents() : Promise.resolve({ success: false, events: [] }),
        apiService().query('getEmployeesHolidays', `id=${workerId}`),
        apiService().query('getWorkerContracts', `id=${workerId}`),
        apiService().query('getWorkerSalary', `workerId=${workerId}`),
      ]);
      if (teR.success) setTimeEntries((teR.data || []).filter(e => String(e.worker_id) === String(workerId)));
      else setTimeEntries([]);
      const events = evR.events || evR.data || [];
      setPresEvents(events.filter(e => String(e.WorkerId || e.workerId) === String(workerId)));
      setHolidays(hR.data || hR.holidays || []);
      const contracts = cR.data || [];
      setContract(contracts.length ? contracts.sort((a, b) => new Date(b.StartDate || b.startDate) - new Date(a.StartDate || a.startDate))[0] : null);
      const sal = sR.data;
      setSalary(Array.isArray(sal) ? sal[0] : sal);
      // load project assignment history (authFetch reads the token from the
      // right storage — sessionStorage/cookie — and refreshes on 401)
      authFetch(`/api/workers/${workerId}/project-assignments`)
        .then(r => r.json()).then(d => setProjectAssignments(d.data || [])).catch(() => setProjectAssignments([]));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  // Default selection: every user lands on their own profile first. Privileged
  // users can then switch to any worker; non-privileged stay locked to theirs.
  const didDefault = useRef(false);
  useEffect(() => {
    if (didDefault.current || !myWorkerId) return;
    if (!canViewOthers) {                 // locked to own card — set immediately
      setSelectedId(String(myWorkerId));
      didDefault.current = true;
      return;
    }
    if (workers.length) {                 // privileged — default once list is ready
      if (workers.some(w => String(w.Id || w.id) === String(myWorkerId))) {
        setSelectedId(String(myWorkerId));
      }
      didDefault.current = true;
    }
  }, [canViewOthers, myWorkerId, workers]);

  useEffect(() => {
    if (selectedId) loadWorkerData(selectedId);
    else { setTimeEntries([]); setPresEvents([]); setHolidays([]); setContract(null); setSalary(null); setProjectAssignments([]); }
  }, [selectedId, loadWorkerData]);

  // selected worker object
  const worker = useMemo(() => workers.find(w => String(w.Id || w.id) === String(selectedId)), [workers, selectedId]);
  // Viewing your own card → the name is redundant, show "Profili im" instead.
  const isSelf = !!selectedId && String(selectedId) === String(myWorkerId);

  // ── Self-service profile editing ────────────────────────────────────────────
  // Every user can fill in / update the profile fields that belong to them.
  // Employment, salary, role and ATK stay admin-controlled (read-only here).
  const d10 = (v) => (v ? String(v).slice(0, 10) : '');
  const buildForm = (w) => ({
    FirstName:         w.FirstName || '',
    LastName:          w.LastName || '',
    DateOfBirth:       d10(w.DateOfBirth || w.BirthDate || w.birthday),
    Gender:            w.Gender || '',
    PersonalNumber:    w.PersonalNumber || '',
    Phone:             w.Phone || '',
    Address:           w.Address || '',
    BankAccount:       w.BankAccount || '',
    PensionNumber:     w.PensionNumber || '',
    HealthInsNumber:   w.HealthInsNumber || '',
    HealthInsIssued:   d10(w.HealthInsIssued),
    HealthInsExpires:  d10(w.HealthInsExpires),
    HealthInsPersonal: !!(+w.HealthInsPersonal || 0),
    HealthInsFamily:   !!(+w.HealthInsFamily || 0),
    WorkPermitNumber:  w.WorkPermitNumber || '',
    WorkPermitIssued:  d10(w.WorkPermitIssued),
    WorkPermitExpires: d10(w.WorkPermitExpires),
    Notes:             w.Notes || '',
  });
  const [form,          setForm]          = useState({});
  const [savedForm,     setSavedForm]     = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [snack,         setSnack]         = useState(null);
  const setField = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const setSwitch = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.checked }));
  const profileDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(savedForm), [form, savedForm]);

  // Seed the form whenever the selected worker changes.
  useEffect(() => {
    if (worker && isSelf) { const f = buildForm(worker); setForm(f); setSavedForm(f); }
  }, [worker, isSelf]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveProfile = async () => {
    if (!worker || savingProfile) return;
    setSavingProfile(true);
    try {
      // Send the FULL worker object merged with edits — the backend rebuilds
      // every column on update, so a partial payload would wipe other fields.
      const payload = {
        ...worker,
        Id:                worker.Id || worker.id || selectedId,
        FirstName:         form.FirstName,
        LastName:          form.LastName,
        DateOfBirth:       form.DateOfBirth || null,
        Gender:            form.Gender,
        PersonalNumber:    form.PersonalNumber,
        Phone:             form.Phone,
        Address:           form.Address,
        BankAccount:       form.BankAccount,
        PensionNumber:     form.PensionNumber,
        HealthInsNumber:   form.HealthInsNumber,
        HealthInsIssued:   form.HealthInsIssued || null,
        HealthInsExpires:  form.HealthInsExpires || null,
        HealthInsPersonal: form.HealthInsPersonal ? 1 : 0,
        HealthInsFamily:   form.HealthInsFamily ? 1 : 0,
        WorkPermitNumber:  form.WorkPermitNumber,
        WorkPermitIssued:  form.WorkPermitIssued || null,
        WorkPermitExpires: form.WorkPermitExpires || null,
        Notes:             form.Notes,
      };
      const r = await apiService().put('workers', payload);
      if (r?.success) {
        setSavedForm(form);
        const wR = await apiService().get('workers');
        if (wR.success) setWorkers(wR.data || []);
        setSnack({ severity: 'success', msg: t('report.profileSaved') });
      } else {
        setSnack({ severity: 'error', msg: t('report.profileSaveError') });
      }
    } catch (e) {
      console.error('save profile:', e);
      setSnack({ severity: 'error', msg: t('report.profileSaveError') });
    } finally {
      setSavingProfile(false);
    }
  };
  const deptName = useMemo(() => {
    if (!worker) return '';
    if (worker.DepartmentName) return worker.DepartmentName;
    const d = departments.find(d => String(d.id || d.Id) === String(worker.Department || worker.department_id));
    return d ? (d.name || d.Name || '') : '';
  }, [worker, departments]);
  const branchName = useMemo(() => {
    if (!worker) return '';
    const b = branches.find(b => String(b.id || b.Id) === String(worker.Branch || worker.branch_id));
    return b ? (b.name || b.Name || '') : '';
  }, [worker, branches]);

  // ── filtered time entries ────────────────────────────────────────────────
  const filteredTime = useMemo(() =>
    timeEntries.filter(e => e.date && e.date >= dateFrom && e.date <= dateTo)
      .sort((a, b) => b.date.localeCompare(a.date))
  , [timeEntries, dateFrom, dateTo]);

  const totalMinTime = useMemo(() => filteredTime.reduce((s, e) => s + (e.duration_seconds || 0) / 60, 0), [filteredTime]);
  const workDays     = useMemo(() => new Set(filteredTime.map(e => e.date)).size, [filteredTime]);
  const avgMinPerDay = workDays > 0 ? totalMinTime / workDays : 0;

  // ── filtered presence sessions ───────────────────────────────────────────
  const allSessions = useMemo(() => eventsToSessions(presEvents), [presEvents]);
  const filteredSess = useMemo(() =>
    allSessions.filter(s => s.day >= dateFrom && s.day <= dateTo)
  , [allSessions, dateFrom, dateTo]);

  const presentDays = filteredSess.filter(s => s.checkIn).length;
  const lateDays    = filteredSess.filter(s => { const tm = fmtTime(s.checkIn); return s.checkIn && tm > LATE_THRESHOLD; }).length;

  // ── leave summary ────────────────────────────────────────────────────────
  const leavesByType = useMemo(() => {
    const map = {};
    (holidays || []).forEach(h => {
      const type = String(h.TypeId || h.type_id || h.leaveType || 1);
      if (!map[type]) map[type] = 0;
      map[type]++;
    });
    return map;
  }, [holidays]);

  const filteredLeaves = useMemo(() =>
    (holidays || []).filter(h => {
      const d = h.StartDate || h.start_date || h.date || '';
      return d >= dateFrom && d <= dateTo;
    }).sort((a, b) => (b.StartDate || '').localeCompare(a.StartDate || ''))
  , [holidays, dateFrom, dateTo]);

  // project name helper
  const projectName = useCallback(id => {
    const p = projects.find(p => String(p.id || p.Id) === String(id));
    return p ? (p.name || p.Name || `Projekt #${id}`) : (id ? `Projekt #${id}` : '—');
  }, [projects]);

  // ── export helpers ───────────────────────────────────────────────────────
  const workerFullName = worker ? `${worker.FirstName || ''} ${worker.LastName || ''}`.trim() : t('report.worker');

  function exportTimesheetCSV() {
    const hdrs = [t('report.date'), t('report.hours'), t('report.minutes'), t('report.project'), t('report.activity'), t('report.description')];
    const rows = filteredTime.map(e => [
      e.date, Math.floor((e.duration_seconds || 0) / 3600),
      Math.round(((e.duration_seconds || 0) % 3600) / 60),
      projectName(e.project_id), e.activity || '—', e.description || '',
    ]);
    const lines = [hdrs, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','));
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(lines.join('\n'));
    a.download = `timesheet-${workerFullName}-${dateFrom}.csv`;
    a.click();
  }
  function exportTimesheetExcel() {
    const hdrs = [t('report.date'), t('report.hours'), t('report.min'), t('report.project'), t('report.activity'), t('report.description')];
    const rows = filteredTime.map(e => [
      e.date, Math.floor((e.duration_seconds || 0) / 3600),
      Math.round(((e.duration_seconds || 0) % 3600) / 60),
      projectName(e.project_id), e.activity || '—', e.description || '',
    ]);
    const ws = XLSX.utils.aoa_to_sheet([hdrs, ...rows]);
    ws['!cols'] = [14, 6, 6, 20, 18, 30].map(w => ({ wch: w }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Timesheet');
    XLSX.writeFile(wb, `timesheet-${workerFullName}-${dateFrom}.xlsx`);
  }
  function exportTimesheetPDF() {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13);
    doc.text(`Timesheet — ${workerFullName}`, 14, 15);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
    doc.text(`${t('report.period')}: ${dateFrom} — ${dateTo}  |  ${t('report.total')}: ${fmtHHMM(totalMinTime)}`, 14, 22);
    autoTable(doc, {
      head: [[t('report.date'), t('report.hours'), t('report.min'), t('report.project'), t('report.activity'), t('report.description')]],
      body: filteredTime.map(e => [
        e.date, Math.floor((e.duration_seconds || 0) / 3600),
        Math.round(((e.duration_seconds || 0) % 3600) / 60),
        projectName(e.project_id), e.activity || '—', e.description || '',
      ]),
      startY: 27, styles: { fontSize: 8 }, headStyles: { fillColor: [37, 99, 235] },
    });
    doc.save(`timesheet-${workerFullName}-${dateFrom}.pdf`);
  }

  function exportFullExcel() {
    const wb = XLSX.utils.book_new();
    // Info
    const info = [
      [t('report.fullName'), workerFullName], [t('report.department'), deptName], [t('report.branch'), branchName],
      [t('report.position'), worker?.Position || worker?.position || '—'],
      [t('report.email'), worker?.Email || worker?.email || '—'],
      [t('report.phone'), worker?.Phone || worker?.phone || '—'],
      [t('report.contract'), contract ? (contract.ContractType || contract.type || '—') : '—'],
      [t('report.startDate'), contract ? fmtDate(contract.StartDate || contract.startDate) : '—'],
      [t('report.endDate'), contract ? fmtDate(contract.EndDate || contract.endDate) : '—'],
      [t('report.grossSalary'), salary ? (salary.GrossSalary || salary.grossSalary || salary.Gross || '—') : '—'],
    ];
    const wsInfo = XLSX.utils.aoa_to_sheet(info);
    XLSX.utils.book_append_sheet(wb, wsInfo, t('report.info'));
    // Timesheet
    const tsH = [t('report.date'), t('report.hours'), t('report.min'), t('report.project'), t('report.activity'), t('report.description')];
    const tsR = filteredTime.map(e => [
      e.date, Math.floor((e.duration_seconds || 0) / 3600),
      Math.round(((e.duration_seconds || 0) % 3600) / 60),
      projectName(e.project_id), e.activity || '—', e.description || '',
    ]);
    const wsTs = XLSX.utils.aoa_to_sheet([tsH, ...tsR]);
    XLSX.utils.book_append_sheet(wb, wsTs, 'Timesheet');
    // Presence
    const prH = [t('report.date'), 'Check-In', 'Check-Out', t('report.breakMinParen'), t('report.netHours'), t('report.status')];
    const prR = filteredSess.map(s => {
      const bm   = breakMin(s.breaks);
      const ci   = s.checkIn  ? new Date(s.checkIn)  : null;
      const co   = s.checkOut ? new Date(s.checkOut)  : null;
      const netM = ci && co ? Math.max(0, Math.round((co - ci) / 60000) - bm) : 0;
      const st   = attendStatus(s.checkIn, t);
      return [s.day, fmtTime(s.checkIn), fmtTime(s.checkOut), bm, fmtHHMM(netM), st.label];
    });
    const wsPr = XLSX.utils.aoa_to_sheet([prH, ...prR]);
    XLSX.utils.book_append_sheet(wb, wsPr, t('report.presence'));
    // Leaves
    const lvH = [t('report.startDate'), t('report.endDate'), t('report.type'), t('report.status'), t('report.days')];
    const lvR = filteredLeaves.map(h => [
      fmtDate(h.StartDate || h.start_date), fmtDate(h.EndDate || h.end_date),
      leaveTypes[h.TypeId || h.type_id || h.leaveType] || t('report.other'),
      h.Status || h.status || '—', h.Days || h.days || '—',
    ]);
    const wsLv = XLSX.utils.aoa_to_sheet([lvH, ...lvR]);
    XLSX.utils.book_append_sheet(wb, wsLv, t('report.leaves'));
    XLSX.writeFile(wb, `raport-i-plote-${workerFullName}.xlsx`);
  }

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <ReportPage>
      <PageHeader
        title={t('reports.employeeDetail')}
        subtitle={t('report.employeeDetailSubtitle')}
      />

      {/* Toolbar — identity + filters in one compact card */}
      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 2 }}>
        {selectedId && !loading && worker && (
          <>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              <Avatar sx={{ width: 40, height: 40, fontSize: '0.95rem', fontWeight: 800, bgcolor: '#2563EB', color: 'white' }}>
                {initials(`${worker.FirstName || ''} ${worker.LastName || ''}`)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#0F172A', lineHeight: 1.2 }}>
                  {isSelf ? t('report.myProfile') : `${worker.FirstName || ''} ${worker.LastName || ''}`}
                </Typography>
                <Stack direction="row" spacing={0.75} sx={{ mt: 0.25 }} flexWrap="wrap" useFlexGap>
                  {(worker.Position || worker.position) && (
                    <Chip size="small" label={worker.Position || worker.position} sx={{ height: 20, fontSize: '0.7rem' }} />
                  )}
                  {deptName && <Chip size="small" label={deptName} color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />}
                  <Chip size="small" label={worker.Active !== false ? t('report.active') : t('report.inactive')} color={worker.Active !== false ? 'success' : 'default'} sx={{ height: 20, fontSize: '0.7rem' }} />
                </Stack>
              </Box>
              <Stack direction="row" spacing={1} sx={{ ml: 'auto' }} flexWrap="wrap" useFlexGap>
                <Button size="small" variant="contained" startIcon={<BadgeIcon />} component={Link} to={`/home/reports/employee-card/${selectedId}`} sx={{ textTransform: 'none', fontSize: '0.76rem' }}>
                  {t('report.employeeCardPdf')}
                </Button>
                <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportFullExcel} sx={{ textTransform: 'none', fontSize: '0.76rem' }}>
                  {t('report.downloadFullReport')}
                </Button>
              </Stack>
            </Stack>
            <Divider sx={{ mb: 1.5 }} />
          </>
        )}
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            {/* Locked to the logged-in user on their own profile; admins keep the
                picker only when they're not already viewing their own card. */}
            <TextField
              select
              size="small"
              fullWidth
              label={t('report.worker')}
              value={selectedId}
              onChange={e => setSelectedId(e.target.value)}
              disabled={isSelf || !canViewOthers}
            >
              <MenuItem value=""><em>— {t('report.selectWorkerPlaceholder')} —</em></MenuItem>
              {activeWorkers(workers).map(w => (
                <MenuItem key={w.Id || w.id} value={w.Id || w.id}>
                  {w.FirstName || ''} {w.LastName || ''}
                  {w.Position || w.position ? ` · ${w.Position || w.position}` : ''}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={2.5}>
            <TextField type="date" size="small" fullWidth label={t('report.from')} InputLabelProps={{ shrink: true }} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </Grid>
          <Grid item xs={6} sm={3} md={2.5}>
            <TextField type="date" size="small" fullWidth label={t('report.to')} InputLabelProps={{ shrink: true }} value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </Grid>
        </Grid>
      </Paper>

      {!selectedId && (
        <Paper variant="outlined" sx={{ p: 3.5, borderRadius: 2, textAlign: 'center' }}>
          <PersonIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="subtitle1" color="text.disabled" fontWeight={600}>{t('report.selectWorker')}</Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 0.25 }}>{t('report.employeeDetailHint')}</Typography>
        </Paper>
      )}

      {selectedId && loading && (
        <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
      )}

      {selectedId && !loading && worker && (
        <>
          {/* Summary stats */}
          <Grid container spacing={1.25} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard icon={AccessTimeIcon} label={t('report.totalHoursTimesheet')} value={fmtHHMM(totalMinTime)} color="#2563EB" sub={`${workDays} ${t('report.workDays')}`} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard icon={EventAvailableIcon} label={t('report.presentDays')} value={presentDays} color="#0891B2" sub={`${lateDays} ${t('report.late')}`} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard icon={BeachAccessIcon} label={t('report.leavesPeriod')} value={filteredLeaves.length} color="#16A34A" sub={t('report.requests')} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard icon={AccessTimeIcon} label={t('report.avgPerDay')} value={fmtHHMM(avgMinPerDay)} color="#7C3AED" sub={t('report.loggedHours')} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard
                icon={PaymentsIcon}
                label="Paga bruto"
                value={salary ? `€${salary.GrossSalary || salary.grossSalary || salary.Gross || '—'}` : '—'}
                color="#0F766E"
                sub={contract ? (contract.ContractType || contract.type) : ''}
              />
            </Grid>
          </Grid>

          {/* Tabs */}
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                px: 2, borderBottom: '1px solid', borderColor: 'divider', minHeight: 42,
                '& .MuiTab-root': { fontWeight: 600, fontSize: '0.8rem', textTransform: 'none', minHeight: 42 },
              }}
            >
              <Tab label={t('report.personalInfo')} icon={<PersonIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab label="Timesheet" icon={<AccessTimeIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab label={t('report.presence')} icon={<EventAvailableIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab label={t('report.leaves')} icon={<BeachAccessIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab label={t('report.salaryAndContract')} icon={<PaymentsIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab label={t('report.assets', 'Asetet')} icon={<Inventory2Icon sx={{ fontSize: 16 }} />} iconPosition="start" />
            </Tabs>

            <Box sx={{ p: 2 }}>

              {/* ── Tab 0: Personal Info ─────────────────────────────────────── */}
              <TabPanel value={tab} index={0}>
                <Grid container spacing={3}>
                  {isSelf ? (
                    <Grid item xs={12}>
                      {/* Editable self-service profile */}
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#1E293B' }}>{t('report.myData')}</Typography>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={savingProfile ? <CircularProgress size={14} color="inherit" /> : <SaveRoundedIcon />}
                          disabled={!profileDirty || savingProfile}
                          onClick={handleSaveProfile}
                          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                        >
                          {t('report.saveChanges')}
                        </Button>
                      </Stack>

                      {/* Personale */}
                      <Typography variant="caption" fontWeight={700} sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6 }}>{t('report.personalData')}</Typography>
                      <Grid container spacing={2} sx={{ mt: 0.25, mb: 2 }}>
                        <Grid item xs={12} sm={6} md={4}><TextField label={t('report.firstName')} size="small" fullWidth value={form.FirstName || ''} onChange={setField('FirstName')} /></Grid>
                        <Grid item xs={12} sm={6} md={4}><TextField label={t('report.lastName')} size="small" fullWidth value={form.LastName || ''} onChange={setField('LastName')} /></Grid>
                        <Grid item xs={12} sm={6} md={4}><TextField label={t('report.personalNumber')} size="small" fullWidth value={form.PersonalNumber || ''} onChange={setField('PersonalNumber')} /></Grid>
                        <Grid item xs={12} sm={6} md={4}><TextField label={t('report.birthDate')} type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.DateOfBirth || ''} onChange={setField('DateOfBirth')} /></Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField label={t('report.gender')} select size="small" fullWidth value={form.Gender || ''} onChange={setField('Gender')}>
                            <MenuItem value=""><em>—</em></MenuItem>
                            <MenuItem value="M">{t('report.genderM')}</MenuItem>
                            <MenuItem value="F">{t('report.genderF')}</MenuItem>
                          </TextField>
                        </Grid>
                      </Grid>

                      {/* Kontakt */}
                      <Typography variant="caption" fontWeight={700} sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6 }}>{t('report.contact')}</Typography>
                      <Grid container spacing={2} sx={{ mt: 0.25, mb: 2 }}>
                        <Grid item xs={12} sm={6} md={4}><TextField label={t('report.email')} size="small" fullWidth value={worker.Email || ''} disabled helperText={t('report.adminManaged')} /></Grid>
                        <Grid item xs={12} sm={6} md={4}><TextField label={t('report.phone')} size="small" fullWidth value={form.Phone || ''} onChange={setField('Phone')} /></Grid>
                        <Grid item xs={12} sm={6} md={4}><TextField label={t('report.address')} size="small" fullWidth value={form.Address || ''} onChange={setField('Address')} /></Grid>
                      </Grid>

                      {/* Banka */}
                      <Typography variant="caption" fontWeight={700} sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6 }}>{t('report.bank')}</Typography>
                      <Grid container spacing={2} sx={{ mt: 0.25, mb: 2 }}>
                        <Grid item xs={12} sm={6} md={4}><TextField label={t('report.bankAccount')} size="small" fullWidth value={form.BankAccount || ''} onChange={setField('BankAccount')} /></Grid>
                        <Grid item xs={12} sm={6} md={4}><TextField label={t('report.pensionNumber')} size="small" fullWidth value={form.PensionNumber || ''} onChange={setField('PensionNumber')} /></Grid>
                      </Grid>

                      {/* Sigurimi shëndetësor */}
                      <Typography variant="caption" fontWeight={700} sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6 }}>{t('report.healthInsurance')}</Typography>
                      <Grid container spacing={2} sx={{ mt: 0.25, mb: 1 }}>
                        <Grid item xs={12} sm={6} md={4}><TextField label={t('report.insNumber')} size="small" fullWidth value={form.HealthInsNumber || ''} onChange={setField('HealthInsNumber')} /></Grid>
                        <Grid item xs={12} sm={6} md={4}><TextField label={t('report.issued')} type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.HealthInsIssued || ''} onChange={setField('HealthInsIssued')} /></Grid>
                        <Grid item xs={12} sm={6} md={4}><TextField label={t('report.expires')} type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.HealthInsExpires || ''} onChange={setField('HealthInsExpires')} /></Grid>
                        <Grid item xs={12}>
                          <FormControlLabel control={<Switch checked={!!form.HealthInsPersonal} onChange={setSwitch('HealthInsPersonal')} />} label={t('report.healthPersonal')} />
                          <FormControlLabel control={<Switch checked={!!form.HealthInsFamily} onChange={setSwitch('HealthInsFamily')} />} label={t('report.healthFamily')} />
                        </Grid>
                      </Grid>

                      {/* Leja e punës */}
                      <Typography variant="caption" fontWeight={700} sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6 }}>{t('report.workPermit')}</Typography>
                      <Grid container spacing={2} sx={{ mt: 0.25, mb: 2 }}>
                        <Grid item xs={12} sm={6} md={4}><TextField label={t('report.insNumber')} size="small" fullWidth value={form.WorkPermitNumber || ''} onChange={setField('WorkPermitNumber')} /></Grid>
                        <Grid item xs={12} sm={6} md={4}><TextField label={t('report.issued')} type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.WorkPermitIssued || ''} onChange={setField('WorkPermitIssued')} /></Grid>
                        <Grid item xs={12} sm={6} md={4}><TextField label={t('report.expires')} type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.WorkPermitExpires || ''} onChange={setField('WorkPermitExpires')} /></Grid>
                      </Grid>

                      {/* Shënime */}
                      <TextField label={t('report.notes')} size="small" fullWidth multiline minRows={2} value={form.Notes || ''} onChange={setField('Notes')} sx={{ mb: 2.5 }} />

                      {/* Admin-controlled (read-only) */}
                      <Divider sx={{ mb: 1.5 }} />
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                        <LockRoundedIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                        <Typography variant="caption" fontWeight={700} sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6 }}>{t('report.positionAndOrg')}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>· {t('report.adminManaged')}</Typography>
                      </Stack>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <InfoRow label={t('report.department')} value={deptName} />
                          <InfoRow label={t('report.branch')} value={branchName} />
                          <InfoRow label={t('report.position')} value={worker.Position || worker.position} />
                          <InfoRow label={t('report.role')} value={worker.Role || worker.role} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <InfoRow label={t('report.startDate')} value={fmtDate(worker.StartDate || worker.startDate || worker.HireDate || worker.hireDate)} />
                          <InfoRow label={t('report.atkRegistered')} value={worker.ATKRegistered ? t('report.yes') : t('report.no')} />
                          <InfoRow label={t('report.atkRegistrationDate')} value={fmtDate(worker.ATKRegisteredAt)} />
                        </Grid>
                      </Grid>
                    </Grid>
                  ) : (
                  <>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#1E293B' }}>{t('report.personalData')}</Typography>
                    <InfoRow label={t('report.fullName')} value={`${worker.FirstName || ''} ${worker.LastName || ''}`} />
                    <InfoRow label={t('report.fatherName')} value={worker.FatherName || worker.fatherName} />
                    <InfoRow label={t('report.email')} value={worker.Email || worker.email} />
                    <InfoRow label={t('report.phone')} value={worker.Phone || worker.phone} />
                    {/* Fusha lexohej si `BirthDate`/`birthDate` — dy emra që API-ja
                        nuk i kthen kurrë (kolona është `date_of_birth` → `DateOfBirth`),
                        ndaj datëlindja ishte gjithmonë «—» te «Profili im». Lista e
                        emrave është tani e njëjta si te forma e redaktimit poshtë. */}
                    <InfoRow label={t('report.birthDate')} value={(() => {
                      const b = worker.DateOfBirth || worker.BirthDate || worker.Birthday
                        || worker.dateOfBirth || worker.birthDate || worker.birthday;
                      const age = ageFromBirthDate(b);
                      const shown = fmtDate(b);
                      if (!shown) return '';
                      return age === null ? shown : `${shown} · ${age} ${t('report.yearsOld')}`;
                    })()} />
                    <InfoRow label={t('report.personalNumber')} value={worker.PersonalNumber || worker.personalNumber} />
                    <InfoRow label={t('report.address')} value={worker.Address || worker.address} />
                    <InfoRow label={t('report.city')} value={worker.City || worker.city} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#1E293B' }}>{t('report.positionAndOrg')}</Typography>
                    <InfoRow label={t('report.department')} value={deptName} />
                    <InfoRow label={t('report.branch')} value={branchName} />
                    <InfoRow label={t('report.position')} value={worker.Position || worker.position} />
                    <InfoRow label={t('report.role')} value={worker.Role || worker.role} />
                    <InfoRow label={t('report.startDate')} value={fmtDate(worker.StartDate || worker.startDate || worker.HireDate || worker.hireDate)} />
                    <InfoRow label={t('report.atkNumber')} value={worker.ATKNumber || worker.atkNumber} />
                    <InfoRow label={t('report.workNumber')} value={worker.WorkNumber || worker.workNumber} />
                    <InfoRow label={t('report.ibanBank')} value={worker.IBAN || worker.iban} />
                  </Grid>
                  </>
                  )}
                  {contract && (
                    <Grid item xs={12}>
                      <Divider sx={{ mb: 2 }} />
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#1E293B' }}>{t('report.activeContract')}</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                          <InfoRow label={t('report.type')} value={contract.ContractType || contract.type} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <InfoRow label={t('report.startDate')} value={fmtDate(contract.StartDate || contract.startDate)} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <InfoRow label={t('report.endDate')} value={fmtDate(contract.EndDate || contract.endDate)} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <InfoRow label={t('report.hoursPerWeek')} value={contract.WeeklyHours || contract.weeklyHours} />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                  {projectAssignments.length > 0 && (
                    <Grid item xs={12}>
                      <Divider sx={{ mb: 2 }} />
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#1E293B' }}>
                        {t('report.projectAssignmentHistory')}
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#F0F9FF' }}>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.78rem' }}>{t('report.project')}</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.78rem' }}>{t('report.startDate')}</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.78rem' }}>{t('report.endDate')}</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.78rem' }}>{t('report.status')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {projectAssignments.map((pa) => (
                            <TableRow key={pa.id} hover>
                              <TableCell sx={{ fontSize: '0.82rem', fontWeight: 500 }}>{pa.project_name || `#${pa.project_id}`}</TableCell>
                              <TableCell sx={{ fontSize: '0.82rem' }}>{fmtDate(pa.start_date)}</TableCell>
                              <TableCell sx={{ fontSize: '0.82rem' }}>{pa.end_date ? fmtDate(pa.end_date) : '—'}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={pa.end_date ? t('report.completed') : t('report.active')}
                                  color={pa.end_date ? 'default' : 'success'}
                                  sx={{ fontSize: '0.72rem' }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Grid>
                  )}
                </Grid>
              </TabPanel>

              {/* ── Tab 1: Timesheet ─────────────────────────────────────────── */}
              <TabPanel value={tab} index={1}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AccessTimeIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Typography variant="subtitle2" fontWeight={700}>{t('report.loggedHoursLabel')}</Typography>
                    <Chip size="small" label={`${filteredTime.length} ${t('report.entries')}`} sx={{ fontSize: '0.72rem' }} />
                    <Chip size="small" label={`${t('report.total')}: ${fmtHHMM(totalMinTime)}`} color="primary" variant="outlined" sx={{ fontSize: '0.72rem' }} />
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <ExportMenu onCsv={exportTimesheetCSV} onExcel={exportTimesheetExcel} onPdf={exportTimesheetPDF} />
                  </Stack>
                </Stack>
                <Box sx={{ overflowX: 'auto', maxHeight: 480, overflowY: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {[t('report.date'), t('report.loggedTime'), t('report.project'), t('report.activity'), t('report.description'), t('report.pctVs8h')].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.78rem', bgcolor: 'grey.50', whiteSpace: 'nowrap' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredTime.length === 0 ? (
                        <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.secondary' }}>{t('report.noDataForPeriod')}</TableCell></TableRow>
                      ) : filteredTime.map((e, i) => {
                        const min  = (e.duration_seconds || 0) / 60;
                        const pct  = Math.min(100, Math.round((min / STANDARD_MIN) * 100));
                        const pColor = pct >= 90 ? 'success' : pct >= 75 ? 'info' : pct >= 50 ? 'warning' : 'error';
                        return (
                          <TableRow key={e.id || i} hover sx={{ bgcolor: i % 2 ? '#FAFAFA' : 'white' }}>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{e.date}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.83rem', whiteSpace: 'nowrap' }}>{fmtHHMM(min)}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{projectName(e.project_id)}</TableCell>
                            <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{e.activity || '—'}</TableCell>
                            <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.description || '—'}</TableCell>
                            <TableCell sx={{ minWidth: 100 }}>
                              <Stack spacing={0.3}>
                                <Typography variant="caption" fontWeight={700} color={`${pColor}.main`}>{pct}%</Typography>
                                <LinearProgress variant="determinate" value={pct} color={pColor} sx={{ height: 5, borderRadius: 4 }} />
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
                {filteredTime.length > 0 && (
                  <Box sx={{ px: 2, py: 1.25, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" spacing={3}>
                      <Typography variant="caption" color="text.secondary">{t('report.totalHoursLabel')}: <strong>{fmtHHMM(totalMinTime)}</strong></Typography>
                      <Typography variant="caption" color="text.secondary">{t('report.workDays')}: <strong>{workDays}</strong></Typography>
                      <Typography variant="caption" color="text.secondary">{t('report.avgPerDay')}: <strong>{fmtHHMM(avgMinPerDay)}</strong></Typography>
                    </Stack>
                  </Box>
                )}
              </TabPanel>

              {/* ── Tab 2: Presence ──────────────────────────────────────────── */}
              <TabPanel value={tab} index={2}>
                <Stack direction="row" alignItems="center" sx={{ mb: 2 }} spacing={1}>
                  <EventAvailableIcon sx={{ fontSize: 18, color: '#0891B2' }} />
                  <Typography variant="subtitle2" fontWeight={700}>{t('report.presenceLog')}</Typography>
                  <Chip size="small" label={`${filteredSess.length} ${t('report.days')}`} sx={{ fontSize: '0.72rem' }} />
                  <Chip size="small" label={`${presentDays} ${t('report.present')}`} color="success" variant="outlined" sx={{ fontSize: '0.72rem' }} />
                  {lateDays > 0 && <Chip size="small" label={`${lateDays} ${t('report.late')}`} color="warning" variant="outlined" sx={{ fontSize: '0.72rem' }} />}
                </Stack>
                <Box sx={{ overflowX: 'auto', maxHeight: 480, overflowY: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {[t('report.date'), 'Check-In', 'Check-Out', t('report.break'), t('report.netHours'), t('report.status')].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.78rem', bgcolor: 'grey.50', whiteSpace: 'nowrap' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredSess.length === 0 ? (
                        <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.secondary' }}>{t('report.noPresenceLog')}</TableCell></TableRow>
                      ) : filteredSess.map((s, i) => {
                        const bm   = breakMin(s.breaks);
                        const ci   = s.checkIn  ? new Date(s.checkIn)  : null;
                        const co   = s.checkOut ? new Date(s.checkOut)  : null;
                        const netM = ci && co ? Math.max(0, Math.round((co - ci) / 60000) - bm) : 0;
                        const st   = attendStatus(s.checkIn, t);
                        return (
                          <TableRow key={s.day} hover sx={{ bgcolor: i % 2 ? '#FAFAFA' : 'white' }}>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{s.day}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{fmtTime(s.checkIn)}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{fmtTime(s.checkOut)}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{bm > 0 ? `${bm}min` : '—'}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.83rem' }}>{netM > 0 ? fmtHHMM(netM) : '—'}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={st.label}
                                color={st.color}
                                icon={st.color === 'success' ? <CheckCircleIcon /> : st.color === 'warning' ? <WatchLaterIcon /> : <CancelIcon />}
                                sx={{ fontSize: '0.72rem', height: 22 }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </TabPanel>

              {/* ── Tab 3: Leaves ────────────────────────────────────────────── */}
              <TabPanel value={tab} index={3}>
                <Grid container spacing={2} sx={{ mb: 2.5 }}>
                  {Object.entries(leaveTypes).map(([typeId, typeName]) => {
                    const count = leavesByType[typeId] || 0;
                    return (
                      <Grid item xs={6} sm={4} md={3} key={typeId}>
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, textAlign: 'center' }}>
                          <Typography variant="h5" fontWeight={800} color={count > 0 ? 'primary.main' : 'text.disabled'}>{count}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>{typeName}</Typography>
                          <Typography variant="caption" display="block" color="text.disabled" sx={{ fontSize: '0.68rem' }}>{t('report.totalDaysHistory')}</Typography>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
                <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }} spacing={1}>
                  <BeachAccessIcon sx={{ fontSize: 18, color: '#16A34A' }} />
                  <Typography variant="subtitle2" fontWeight={700}>{t('report.leaveRequestsPeriod')}</Typography>
                  <Chip size="small" label={`${filteredLeaves.length} ${t('report.requests')}`} sx={{ fontSize: '0.72rem' }} />
                </Stack>
                <Box sx={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {[t('report.from'), t('report.to'), t('report.type'), t('report.days'), t('report.status'), t('report.reason')].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.78rem', bgcolor: 'grey.50', whiteSpace: 'nowrap' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredLeaves.length === 0 ? (
                        <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.secondary' }}>{t('report.noLeavesForPeriod')}</TableCell></TableRow>
                      ) : filteredLeaves.map((h, i) => {
                        const status = h.Status || h.status || 'pending';
                        const sColor = status === 'approved' || status === 'Aprovuar' ? 'success'
                          : status === 'rejected' || status === 'Refuzuar' ? 'error' : 'warning';
                        return (
                          <TableRow key={h.Id || h.id || i} hover sx={{ bgcolor: i % 2 ? '#FAFAFA' : 'white' }}>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{fmtDate(h.StartDate || h.start_date)}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{fmtDate(h.EndDate || h.end_date)}</TableCell>
                            <TableCell>
                              <Chip size="small" label={leaveTypes[h.TypeId || h.type_id || h.leaveType] || t('report.other')} variant="outlined" sx={{ fontSize: '0.73rem', height: 22 }} />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.82rem', textAlign: 'center' }}>{h.Days || h.days || '—'}</TableCell>
                            <TableCell>
                              <Chip size="small" label={status} color={sColor} sx={{ fontSize: '0.72rem', height: 22, textTransform: 'capitalize' }} />
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {h.Reason || h.reason || h.Note || h.note || '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </TabPanel>

              {/* ── Tab 4: Salary & Contract ─────────────────────────────────── */}
              <TabPanel value={tab} index={4}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: '#1E293B' }}>
                      <PaymentsIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle', color: '#0F766E' }} />
                      {t('report.currentSalary')}
                    </Typography>
                    {salary ? (
                      <>
                        <InfoRow label={t('report.grossSalary')} value={`€${salary.GrossSalary || salary.grossSalary || salary.Gross || '—'}`} />
                        <InfoRow label={t('report.netSalary')} value={`€${salary.NetSalary || salary.netSalary || salary.Net || '—'}`} />
                        <InfoRow label={t('report.incomeTax')} value={`€${salary.TaxAmount || salary.taxAmount || salary.Tax || '—'}`} />
                        <InfoRow label={t('report.pensionEmployee')} value={`€${salary.PensionEmployee || salary.pensionEmployee || '—'}`} />
                        <InfoRow label={t('report.pensionEmployer')} value={`€${salary.PensionEmployer || salary.pensionEmployer || '—'}`} />
                        <InfoRow label={t('report.overtimePay')} value={`€${salary.OvertimePay || salary.overtimePay || 0}`} />
                        <InfoRow label={t('report.month')} value={salary.Month || salary.month || salary.Period || '—'} />
                        <InfoRow label={t('report.status')} value={salary.Status || salary.status || '—'} />
                      </>
                    ) : (
                      <Typography variant="body2" color="text.disabled" sx={{ py: 2 }}>{t('report.noSalaryData')}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: '#1E293B' }}>
                      <BadgeIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle', color: '#7C3AED' }} />
                      {t('report.activeContract')}
                    </Typography>
                    {contract ? (
                      <>
                        <InfoRow label={t('report.contractType')} value={contract.ContractType || contract.type} />
                        <InfoRow label={t('report.startDate')} value={fmtDate(contract.StartDate || contract.startDate)} />
                        <InfoRow label={t('report.endDate')} value={fmtDate(contract.EndDate || contract.endDate)} />
                        <InfoRow label={t('report.hoursPerWeek')} value={contract.WeeklyHours || contract.weeklyHours} />
                        <InfoRow label={t('report.probationEnd')} value={contract.ProbationEnd || contract.probationEnd ? fmtDate(contract.ProbationEnd || contract.probationEnd) : '—'} />
                        <InfoRow label={t('report.status')} value={contract.Status || contract.status || '—'} />
                        <InfoRow label={t('report.positionContract')} value={contract.Position || contract.position} />
                        <InfoRow label={t('report.notes')} value={contract.Notes || contract.notes} />
                      </>
                    ) : (
                      <Typography variant="body2" color="text.disabled" sx={{ py: 2 }}>{t('report.noActiveContract')}</Typography>
                    )}
                  </Grid>
                </Grid>
              </TabPanel>

              {/* ── Tab 5: Asetet ────────────────────────────────────────────── */}
              {/* Këtu lexohet paralajmërimi i largimit: paneli e thotë me shifra
                  sa pajisje mbeten pa u kthyer. Sistemi s'ka ekran "largim" ku
                  të rrinte — `workers.active` ndryshohet nga listat — ndaj rri
                  aty ku HR-ja shkon gjithsesi para se ta mbyllë dosjen. */}
              <TabPanel value={tab} index={5}>
                <WorkerAssetsPanel workerId={selectedId} self={isSelf} />
              </TabPanel>

            </Box>
          </Paper>
        </>
      )}

      <Snackbar
        open={!!snack}
        autoHideDuration={3500}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? (
          <Alert severity={snack.severity} variant="filled" onClose={() => setSnack(null)} sx={{ width: '100%' }}>
            {snack.msg}
          </Alert>
        ) : null}
      </Snackbar>
    </ReportPage>
  );
}
